import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { SpinnerLoader } from '../../../components/common/loading/SpinnerLoader';
import { PopupConfirmModal } from '../../../components/common/modal/popup/PopupConfirmModal';
import { managedFarmPageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { useLayout } from '../../../hooks/screen/useLayout';
import { useAppDispatch, useAppSelector } from '../../../hooks/store/useStore';
import { MainHead } from '../../../components/common/head/MainHead';
import { useNotification } from '../../../provider/ToastNotificationProvider';
import { managedFarmSlice } from '../../../store/managedFarmSlice';

interface ManagedFarmFormWrapperProps {
  headerTitle: string;
  description?: string;
  children: JSX.Element;
  isLoading?: boolean;
  canConfirm: boolean;
  confirmHandler?: () => void;
  closeHandler?: () => void;
  confirmButtonText?: string;
  prevButtonHandler?: () => void;
  prevButtonText?: string;
  steps?: string[];
  currentStep?: string;
}

export const ManagedFarmFormWrapper = ({
  headerTitle,
  description,
  children,
  isLoading,
  canConfirm,
  confirmHandler,
  closeHandler,
  confirmButtonText = '다음',
  prevButtonHandler,
  prevButtonText = '이전',
  steps,
  currentStep,
}: ManagedFarmFormWrapperProps) => {
  const location = useLocation();
  const notification = useNotification();
  const navigate = useNavigate();

  const [isCloseButtonClicked, setIsCloseButtonClicked] = useState<boolean>(false);

  const managedFarmSelector = useAppSelector((state) => state.managedFarm);
  const { resetState } = managedFarmSlice.actions;
  const dispatch = useAppDispatch();

  const handleSurveyCancelButtonClick = () => {
    if (closeHandler) {
      closeHandler();
      return;
    }
    setIsCloseButtonClicked(true);
  };

  useLayout({
    fixedHeader: (
      <>
        <MainHead backButton={{ isShow: false }} closeButton={{ closeHandler: handleSurveyCancelButtonClick }} />
        {steps && (
          <StepBarContainer>
            {steps.map((step) => {
              return <StepBarItem key={step} current={step === currentStep} />;
            })}
          </StepBarContainer>
        )}
      </>
    ),
    fixedFooter: (
      <ButtonSection>
        <BlurredSection />
        {prevButtonHandler && (
          <PrevButton
            type={'button'}
            disabled={isLoading}
            onClick={() => {
              if (prevButtonHandler) {
                prevButtonHandler();
              }
            }}
          >
            <PrevButtonText>{prevButtonText}</PrevButtonText>
          </PrevButton>
        )}
        <ConfirmButton
          type={'button'}
          disabled={!canConfirm || isLoading}
          onClick={() => {
            if (confirmHandler) {
              confirmHandler();
            }
          }}
        >
          <SpinnerLoader isVisible={isLoading ?? false} />
          {!isLoading && <ConfirmButtonText>{confirmButtonText}</ConfirmButtonText>}
        </ConfirmButton>
      </ButtonSection>
    ),
    fixedHeaderHeight: 82,
    fixedFooterHeight: 72,
    shouldShowFooter: false,
  });

  useEffect(() => {
    if (!managedFarmSelector.address) {
      notification.openNotification({ message: '비정상적인 접근입니다.' });
      navigate(managedFarmPageInfo.entryPage.path, { replace: true });
    }
  }, [managedFarmSelector.address, notification, navigate]);

  return (
    <>
      <Container>
        <TopSection>
          <HeaderSection>
            <Header>{headerTitle}</Header>
            {description && <Description>{description}</Description>}
          </HeaderSection>

          {children}
        </TopSection>
      </Container>

      <PopupConfirmModal
        isShow={isCloseButtonClicked}
        hasCloseButton={false}
        closeModal={() => setIsCloseButtonClicked(false)}
        description={'작성한 내용을 잃게 돼요!\n그래도 중단하고 나가시겠어요?'}
        buttonArray={[
          {
            text: '계속 작성하기',
            onClick: () => setIsCloseButtonClicked(false),
            color: 'blue',
          },
          {
            text: '처음으로',
            onClick: () => {
              navigate(managedFarmPageInfo.entryPage.path, { replace: true });
              dispatch(resetState());
            },
            color: 'gray',
          },
        ]}
      />
    </>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;

  width: 100%;
  padding-bottom: 40px;
`;

const BlurredSection = styled.div`
  width: 100%;
  height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: var(--radius-large, 16px);
  gap: var(--radius-small, 4px);

  border-radius: var(--radius-none, 0px);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 63.96%);

  position: absolute;
  top: -40px;
`;

const TopSection = styled.div`
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;

  overflow: hidden;
`;

const CloseButtonSection = styled.div`
  width: 100%;
  height: 36px;

  display: flex;
  justify-content: flex-end;
  align-items: center;

  padding: 0px 16px;
`;

const CloseButton = styled.button``;

const HeaderSection = styled.div`
  display: flex;
  padding: 24px 0px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const Header = styled.pre`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/XX-Large/xxl-bold */
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 31.2px */

  word-break: break-all;
`;

const Description = styled.pre`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  text-align: center;

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */
`;

const ButtonSection = styled.div`
  position: relative;
  display: flex;
  width: 100%;

  padding: 0px 16px 32px 16px;
  background-color: white;

  justify-content: center;
  align-items: center;

  gap: 8px;
`;

const Button = styled.button`
  width: 100%;
  height: 40px;
  max-height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 16px;

  gap: 4px;

  border-radius: var(--radius-medium, 8px);

  &:disabled {
    opacity: 0.3;
  }

  cursor: pointer;
`;

const ButtonText = styled.span`
  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;

const ConfirmButton = styled(Button)`
  background: var(--Light-Scheme-Color-primary-blue, #196eff);
`;

const ConfirmButtonText = styled(ButtonText)`
  color: var(--Light-Scheme-Monochrome-white, #fff);
`;

const PrevButton = styled(Button)`
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const PrevButtonText = styled(ButtonText)`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);
`;

const StepBarContainer = styled.div`
  display: flex;
  padding: var(--radius-large, 16px);
  justify-content: center;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;
`;

const StepBarItem = styled.div<{ current?: boolean }>`
  display: flex;
  height: 2px;
  justify-content: center;
  align-items: flex-start;
  gap: 10px;
  flex: 1 0 0;

  border-radius: var(--radius-small, 4px);
  background: ${(props) => (props.current ? '#505363' : '#e4e4ed')};
`;
